import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Cookies from 'universal-cookie';
import useTelevisorData from '../../hooks/useTelevisorData';
import useTelevisorHub from '../../hooks/useTelevisorHub';
import LogoN from '../../logos/LogoN.svg';
import { getLogo } from '../../logos';
import '../../styles/tv.css';

const clientLogo = getLogo(process.env.REACT_APP_THEME);

const CLIENT_SLOGAN = {
    vaisandTheme: 'VAISAND: PODER EN MOVIMIENTO',
};

// ─── helpers ────────────────────────────────────────────────────────────────

function padZero(n) { return n < 10 ? `0${n}` : `${n}`; }

function formatClock(d) {
    const h = d.getHours();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${padZero(h12)}:${padZero(d.getMinutes())}:${padZero(d.getSeconds())} ${ampm}`;
}

function formatDate(d) {
    return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function safeNum(n) {
    const v = Number(n);
    return isNaN(v) ? 0 : v;
}

function safeLocale(n) {
    const v = Number(n);
    return isNaN(v) ? '—' : v.toLocaleString('es-MX');
}

function parseUtc(isoStr) {
    if (!isoStr) return null;
    const s = isoStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoStr) ? isoStr : `${isoStr}Z`;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
}

function formatTime(isoStr) {
    const d = parseUtc(isoStr);
    return d ? formatClock(d) : '—';
}

function kpiClass(value) {
    if (value === null || value === undefined || value === '') return '';
    const n = parseFloat(value);
    if (n >= 80) return 'tv--good';
    if (n >= 50) return 'tv--warn';
    return 'tv--bad';
}

function medalInfo(pct) {
    const n = parseFloat(pct);
    if (!isNaN(n) && n >= 95) return { emoji: '🥇', label: 'Excelente',   cls: 'tv-medal--gold'   };
    if (!isNaN(n) && n >= 80) return { emoji: '🥈', label: 'Muy bien',    cls: 'tv-medal--silver' };
    if (!isNaN(n) && n >= 60) return { emoji: '🥉', label: 'En progreso', cls: 'tv-medal--bronze' };
    return                           { emoji: '💪', label: 'A mejorar',   cls: 'tv-medal--push'   };
}

/** Convierte segundos → "Xh Ymin" o "Ymin" o "—" */
function formatSeconds(sec) {
    const v = safeNum(sec);
    if (!v) return '—';
    const h = Math.floor(v / 3600);
    const m = Math.floor((v % 3600) / 60);
    if (h > 0) return `${h}h ${m}min`;
    return `${m}min`;
}

const ESTATUS_MAP = {
    0: { label: 'Fuera de operación', cls: 'tv-status--off'      },
    1: { label: 'Operando',           cls: 'tv-status--running'   },
    2: { label: 'Setup',              cls: 'tv-status--setup'     },
    3: { label: 'Limpieza',           cls: 'tv-status--planned'   },
    4: { label: 'Paro Planeado',      cls: 'tv-status--planned'   },
    5: { label: 'Paro Planeado',      cls: 'tv-status--planned'   },
};

function getEstatusInfo(codigo) {
    return ESTATUS_MAP[codigo] ?? (codigo >= 6 ? { label: 'Paro', cls: 'tv-status--unplanned' } : ESTATUS_MAP[0]);
}

function elapsedHMS(isoStr) {
    const d = parseUtc(isoStr);
    if (!d) return '—';
    const elapsed = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    return [Math.floor(elapsed / 3600), Math.floor((elapsed % 3600) / 60), elapsed % 60]
        .map((n) => String(n).padStart(2, '0')).join(':');
}

// ─── componente principal ────────────────────────────────────────────────────

export default function TVView() {
    const history  = useHistory();
    const cookie   = new Cookies();
    const linea    = process.env.REACT_APP_TV_LINEA || cookie.get('linea');

    const {
        infoInicial,
        indicadores,
        historial,
        llamadasActivas,
        conteoHistorico,
        conteoCorridaActual,
        setConteoCorridaActual,
        loading,
    } = useTelevisorData(linea);

    const { tvStatus, tvLlamadas, tvConteo } = useTelevisorHub(linea);

    const [time, setTime] = useState(new Date());

    // Inyecta --menu-bg según tema
    useEffect(() => {
        const theme = process.env.REACT_APP_THEME;
        let menuBg = '#3F4A51';
        if (theme === 'novaTheme') menuBg = '#5E3F94';
        else if (theme === 'tecnoTheme' || theme === 'colTheme') menuBg = '#3F4A51';
        else if (theme === 'matteTheme') menuBg = '#253139';
        else if (theme === 'posTheme') menuBg = '#00638D';
        else if (theme === 'vaisandTheme') menuBg = '#001F66';
        document.documentElement.style.setProperty('--menu-bg', menuBg);
    }, []);

    useEffect(() => {
        const t = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // Cuando el hub manda Conteo, actualizar conteoCorridaActual
    useEffect(() => {
        if (tvConteo !== null) setConteoCorridaActual(tvConteo);
    }, [tvConteo, setConteoCorridaActual]);

    // ── guard ────────────────────────────────────────────────────────────────
    if (!linea) {
        return (
            <div className="tv-no-config">
                <p>Estación sin configurar</p>
                <span>Seleccione una línea en el panel de configuración.</span>
                <button className="tv-no-config-btn" onClick={() => history.push('/')}>
                    Volver al inicio
                </button>
            </div>
        );
    }

    // ── datos derivados ──────────────────────────────────────────────────────
    const corridaActual = infoInicial?.corridaActual ?? null;
    const nombreLinea   = infoInicial?.nombreLinea ?? cookie.get('nombreL') ?? 'Línea';

    // Conteo total del turno
    const producido = safeNum(conteoHistorico) + safeNum(conteoCorridaActual);
    const meta      = safeNum(infoInicial?.metaTurno);
    const progreso  = meta > 0 ? Math.min(100, Math.round((producido / meta) * 100)) : 0;

    // Estatus: hub tiene prioridad; si no, usar estatusCelda del GetInfoInicial
    const estatusCeldaInicial = infoInicial?.estatusCelda;
    const estatusInfo = tvStatus
        ? getEstatusInfo(tvStatus.codigo)
        : getEstatusInfo(estatusCeldaInicial?.codigo ?? 0);
    const statusLabel = tvStatus
        ? tvStatus.nombre
        : (estatusCeldaInicial?.nombre ?? estatusInfo.label);

    // Indicadores: polling (IndicadoresTurno) tiene prioridad sobre GetInfoInicial
    const kpis = indicadores ?? infoInicial?.indicadores ?? null;
    const dispPct  = kpis ? Math.round(safeNum(kpis.disponibilidad) * 100) : null;
    const desempPct = kpis ? Math.round(safeNum(kpis.desempeno) * 100) : null;
    const mtbfFmt  = kpis ? formatSeconds(kpis.mtbf) : '—';
    const mttrFmt  = kpis ? formatSeconds(kpis.mttr) : '—';

    const medal = medalInfo(desempPct);

    // Llamadas: hub snapshot tiene prioridad sobre el inicial
    const llamadas = tvLlamadas ?? llamadasActivas;

    // Calendario: siguiente corrida (la que no es la activa)
    const calendario  = infoInicial?.calendario ?? [];
    const siguienteCorrida = calendario.find(
        (c) => c.ordenProd !== corridaActual?.ordenProd
    ) ?? null;

    // Timeline: historial del polling, fallback al del GetInfoInicial
    const timelineData = historial.length > 0 ? historial : (infoInicial?.historial ?? []);

    // Ticker
    const slogan = CLIENT_SLOGAN[process.env.REACT_APP_THEME] ?? null;
    const tickerSegments = [
        corridaActual?.nombreProducto
            ? `Produciendo: ${corridaActual.nombreProducto}${corridaActual.sku ? ` (SKU ${corridaActual.sku})` : ''}`
            : null,
        siguienteCorrida?.nombreProducto
            ? `A continuación: ${siguienteCorrida.nombreProducto}${siguienteCorrida.meta ? ` — Meta: ${safeLocale(siguienteCorrida.meta)} pzas` : ''}`
            : null,
        slogan,
    ].filter(Boolean);
    const tickerText = tickerSegments.join('          ·          ');

    return (
        <div className="tv-view">

            {/* ── Header ──────────────────────────────────────────────── */}
            <header className="tv-header">
                <button className="tv-nav-btn" onClick={() => history.push('/')} title="Volver al menú">
                    <img src={LogoN} alt="NOVA" className="tv-nav-logo" />
                </button>
                <h1 className="tv-header-line">{nombreLinea}</h1>
                <div className="tv-header-datetime">
                    <span className="tv-header-clock">{formatClock(time)}</span>
                    <span className="tv-header-date">{formatDate(time)}</span>
                </div>
            </header>

            {/* ── Barra de estado con ticker ───────────────────────────── */}
            <div className={`tv-status-bar ${estatusInfo.cls}`}>
                <span className="tv-status-dot" />
                <span className="tv-status-label">{statusLabel}</span>
                {tickerText && (
                    <>
                        <span className="tv-status-sep">|</span>
                        <div className="tv-ticker-wrap">
                            <div className="tv-ticker-move">
                                <span className="tv-ticker-text">{tickerText}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="tv-body">

                {/* ── Área principal: producto (izq) + héroe (der) ─────── */}
                <div className="tv-main-area">

                    {/* ── Panel izquierdo ──────────────────────────────── */}
                    <div className="tv-left-panel">

                        {/* Card: Produciendo actualmente */}
                        <div className="tv-producto-card">
                            <span className="tv-producto-section-label">Produciendo actualmente</span>
                            {!loading && corridaActual?.nombreProducto ? (
                                <>
                                    <span className="tv-producto-nombre">
                                        {corridaActual.nombreProducto}
                                    </span>
                                    <div className="tv-producto-fields">
                                        {corridaActual.sku && (
                                            <div className="tv-producto-field">
                                                <span className="tv-producto-field-label">SKU</span>
                                                <span className="tv-producto-field-value">{corridaActual.sku}</span>
                                            </div>
                                        )}
                                        {corridaActual.ordenProd && (
                                            <div className="tv-producto-field">
                                                <span className="tv-producto-field-label">Orden</span>
                                                <span className="tv-producto-field-value">{corridaActual.ordenProd}</span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <span className="tv-producto-empty">
                                    {loading ? 'Cargando…' : 'Sin corrida activa'}
                                </span>
                            )}
                        </div>

                    </div>

                    {/* ── Panel derecho: Héroe + Progreso ──────────────── */}
                    <div className="tv-right-panel">

                        <section className="tv-hero">
                            <div className="tv-hero-block">
                                <span className={`tv-hero-num ${kpiClass(progreso)}`}>
                                    {safeLocale(producido)}
                                </span>
                                <span className="tv-hero-label">Producido</span>
                            </div>
                            <div className="tv-hero-divider">/</div>
                            <div className="tv-hero-block tv-hero-block--right">
                                <span className="tv-hero-num tv-hero-num--target">
                                    {meta > 0 ? safeLocale(meta) : '—'}
                                </span>
                                <span className="tv-hero-label">Meta turno</span>
                            </div>
                            <div className="tv-hero-logo-wrap">
                                <img src={clientLogo} alt="logo" className="tv-hero-logo" />
                            </div>
                        </section>

                        <section className="tv-progress-section">
                            <div className="tv-progress-track">
                                <div
                                    className={`tv-progress-fill ${kpiClass(progreso)}`}
                                    style={{ width: `${progreso}%` }}
                                />
                                <span className="tv-progress-pct">{progreso}%</span>
                            </div>
                        </section>

                    </div>

                </div>

                {/* ── KPI strip (fila completa) ─────────────────────────── */}
                <div className="tv-kpi-strip">
                    <div className={`tv-kpi-card ${kpiClass(dispPct)}`}>
                        <span className="tv-kpi-value">
                            {dispPct !== null ? `${dispPct}%` : '—'}
                        </span>
                        <span className="tv-kpi-label">Disponibilidad</span>
                    </div>
                    <div className={`tv-kpi-card ${kpiClass(desempPct)}`}>
                        <span className="tv-kpi-value">
                            {desempPct !== null ? `${desempPct}%` : '—'}
                        </span>
                        <span className="tv-kpi-label">Desempeño</span>
                    </div>
                    <div className="tv-kpi-card">
                        <span className="tv-kpi-value">{mtbfFmt}</span>
                        <span className="tv-kpi-label">MTBF</span>
                    </div>
                    <div className="tv-kpi-card">
                        <span className="tv-kpi-value">{mttrFmt}</span>
                        <span className="tv-kpi-label">MTTR</span>
                    </div>
                </div>

                {/* ── Timeline (Gantt del turno) ───────────────────────── */}
                {timelineData.length > 0 && (
                    <section className="tv-timeline-section">
                        <span className="tv-timeline-title">Historial del turno</span>
                        <div className="tv-timeline-bar">
                            {(() => {
                                const totalMs = timelineData.reduce((acc, s) => {
                                    const s0 = parseUtc(s.fechaHoraIni)?.getTime() ?? 0;
                                    const s1 = parseUtc(s.fechaHoraFin)?.getTime() ?? Date.now();
                                    return acc + (s1 - s0);
                                }, 0);
                                return timelineData.map((seg) => {
                                    const ini = parseUtc(seg.fechaHoraIni)?.getTime() ?? 0;
                                    const fin = parseUtc(seg.fechaHoraFin)?.getTime() ?? Date.now();
                                    const pct = totalMs > 0
                                        ? Math.max(0.5, ((fin - ini) / totalMs) * 100)
                                        : 0;
                                    const segInfo = getEstatusInfo(seg.estatus);
                                    return (
                                        <div
                                            key={seg.folio}
                                            className={`tv-timeline-seg ${segInfo.cls}`}
                                            style={{ width: `${pct}%` }}
                                            title={segInfo.label}
                                        />
                                    );
                                });
                            })()}
                        </div>
                    </section>
                )}

                {/* ── ANDON (siempre visible) + medalla a la derecha ───── */}
                <section className="tv-andon-section">

                    {/* Llamadas activas */}
                    <div className="tv-andon-content">
                        <div className={`tv-andon-banner${llamadas.length === 0 ? ' tv-andon-banner--ok' : ''}`}>
                            {llamadas.length > 0 && <span className="tv-andon-pulse" />}
                            <span className="tv-andon-title">
                                Llamadas activas ({llamadas.length})
                            </span>
                        </div>

                        <div className="tv-llamadas-table-wrap">
                            {llamadas.length === 0 ? (
                                <span className="tv-andon-empty">Todo en orden</span>
                            ) : (
                                <table className="tv-llamadas-table">
                                    <thead>
                                        <tr>
                                            <th>Llamada</th>
                                            <th>Área</th>
                                            <th>Celda</th>
                                            <th>Nivel</th>
                                            <th>Inicio</th>
                                            <th>Respuesta</th>
                                            <th>Estatus</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...llamadas]
                                            .sort((a, b) => (a.estatus === 1 ? 0 : 1) - (b.estatus === 1 ? 0 : 1))
                                            .map((l) => (
                                            <tr key={`${l.celda}-${l.tipoLlamada}-${l.fechaHoraInicio}`}>
                                                <td>
                                                    <div className="tv-llamadas-nombre-cell">
                                                        <span
                                                            className="tv-llamadas-color-dot"
                                                            style={{ '--dot-color': l.color ?? '#888' }}
                                                        />
                                                        {l.nombreTipoLlamada ?? '—'}
                                                    </div>
                                                </td>
                                                <td>{l.areaAtencion ?? '—'}</td>
                                                <td>{l.nombreCelda ?? '—'}</td>
                                                <td>{l.nivel ?? '—'}</td>
                                                <td className="tv-llamadas-mono">
                                                    {l.estatus === 1
                                                        ? elapsedHMS(l.fechaHoraInicio)
                                                        : formatTime(l.fechaHoraInicio)}
                                                </td>
                                                <td className="tv-llamadas-mono">
                                                    {l.estatus === 2
                                                        ? elapsedHMS(l.fechaHoraRespuesta)
                                                        : formatTime(l.fechaHoraRespuesta)}
                                                </td>
                                                <td>
                                                    <span className={`tv-andon-estatus tv-andon-estatus--${l.estatus}`}>
                                                        {l.estatusText ?? '—'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Medalla de rendimiento */}
                    {medal && (
                        <div className={`tv-producto-medal ${medal.cls}`}>
                            <span className="tv-medal-emoji">{medal.emoji}</span>
                            <div className="tv-medal-info">
                                <span className="tv-medal-label">{medal.label}</span>
                                <span className="tv-medal-sub">Rendimiento del turno</span>
                            </div>
                            <div className="tv-medal-stars">
                                {[1, 2, 3, 4, 5].map(i => {
                                    const filled = desempPct !== null
                                        ? Math.round((desempPct / 100) * 5)
                                        : 0;
                                    return (
                                        <span key={i} className={i <= filled ? 'tv-star tv-star--on' : 'tv-star'}>
                                            ★
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                </section>

            </div>
        </div>
    );
}
